import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ProjectService } from '../../../services/project.service';
import { ThirdPartiesService } from '../../../services/third-parties.service';
import { ApplicationUsersService } from '../../../services/application-users.service';
import { ReportJobsService } from '../../../services/report-jobs.service';
import { ReportHubService } from '../../../services/report-hub.service';
import { ToastService } from '../../../services/toast.service';

interface DropdownOption {
  label: string;
  value: string | number;
}

@Component({
  selector: 'app-work-orders-reports',
  templateUrl: './work-orders-reports.component.html',
  styleUrls: ['./work-orders-reports.component.scss']
})
export class WorkOrdersReportsComponent implements OnInit, OnDestroy {
  reportForm!: FormGroup;
  loading = false;

  // Fechas por defecto
  startDate: Date = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  endDate: Date = new Date();

  projectsDropdown: DropdownOption[] = [];
  thirdPartiesDropdown: DropdownOption[] = [];
  usersDropdown: DropdownOption[] = [];
  statusDropdown: DropdownOption[] = [
    { label: 'Pendiente', value: 'PENDIENTE' },
    { label: 'Pagada', value: 'PAGADA' },
    { label: 'Anulada', value: 'ANULADA' }
  ];

  private reportHubSub!: Subscription;
  private pendingJobId: string | null = null;

  constructor(
    private fb: FormBuilder,
    private _projectService: ProjectService,
    private _thirdPartiesService: ThirdPartiesService,
    private _usersService: ApplicationUsersService,
    private _reportJobsService: ReportJobsService,
    private _reportHubService: ReportHubService,
    private _toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadDropdowns();

    this.reportHubSub = this._reportHubService.reportStatusChanged$.subscribe(notification => {
      if (notification.jobId !== this.pendingJobId) return;

      if (notification.status === 'Completed') {
        this.loading = false;
        this._toastService.showToast('success', 'Reporte de órdenes de trabajo generado exitosamente. Descargando...');
        this._reportJobsService.downloadReport(notification.jobId, notification.fileName || undefined);
        this.pendingJobId = null;
      } else if (notification.status === 'Failed') {
        this.loading = false;
        this._toastService.showToast('error', `Error al generar el reporte: ${notification.errorMessage || 'Desconocido'}`);
        this.pendingJobId = null;
      } else if (notification.status === 'Processing') {
        this._toastService.showToast('info', 'Procesando reporte de órdenes de trabajo...');
      }
    });
  }

  ngOnDestroy(): void {
    this.reportHubSub?.unsubscribe();
  }

  private initForm(): void {
    this.reportForm = this.fb.group({
      startDate: [this.startDate, Validators.required],
      endDate: [this.endDate, Validators.required],
      projectIds: [[]],
      thirdPartyId: [null],
      payerId: [null],
      status: [null],
      includeCancelled: [false]
    });
  }

  // Helper para extraer datos de estructura paginada o array directo
  private extractData(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (response?.data) {
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    }
    return [];
  }

  private loadDropdowns(): void {
    this._projectService.getAll().subscribe({
      next: (resp) => {
        const data = this.extractData(resp);
        this.projectsDropdown = data.map((p: any) => ({
          label: `${p.projectId} - ${p.name}`,
          value: p.id
        }));
      },
      error: () => this.projectsDropdown = []
    });

    this._thirdPartiesService.getAll().subscribe({
      next: (resp) => {
        const data = this.extractData(resp);
        this.thirdPartiesDropdown = data.map((t: any) => ({
          label: `${t.firstName} ${t.lastName} - ${t.typeDocument || 'N/A'}: ${t.documentId || 'N/A'}`.trim(),
          value: t.id
        }));
      },
      error: () => this.thirdPartiesDropdown = []
    });

    this._usersService.getAll().subscribe({
      next: (resp) => {
        const data = this.extractData(resp);
        this.usersDropdown = data.map((u: any) => ({
          label: u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.userName,
          value: u.id
        }));
      },
      error: () => this.usersDropdown = []
    });
  }

  onStartDateChange(event: any): void {
    this.startDate = event;
  }

  onEndDateChange(event: any): void {
    this.endDate = event;
  }

  downloadExcelReport(): void {
    if (this.reportForm.invalid) {
      this.reportForm.markAllAsTouched();
      return;
    }

    const formStartDate = this.reportForm.value.startDate;
    const formEndDate = this.reportForm.value.endDate;

    if (!formStartDate || !formEndDate) {
      this._toastService.showToast('warning', 'Por favor seleccione la fecha de inicio y fin');
      return;
    }

    if (formStartDate > formEndDate) {
      this._toastService.showToast('warning', 'La fecha de inicio debe ser menor o igual a la fecha de fin');
      return;
    }

    this.startDate = formStartDate;
    this.endDate = formEndDate;

    const filters = {
      startDate: formStartDate as Date,
      endDate: formEndDate as Date,
      projectIds: (this.reportForm.value.projectIds || []) as number[],
      thirdPartyId: this.reportForm.value.thirdPartyId as number | null,
      payerId: this.reportForm.value.payerId as string | null,
      status: this.reportForm.value.status as string | null,
      includeCancelled: !!this.reportForm.value.includeCancelled
    };

    this.loading = true;
    this._reportJobsService.queueWorkOrdersReport(filters).subscribe({
      next: (response) => {
        this.pendingJobId = response.jobId;
        this._toastService.showToast('info', 'Generando reporte de órdenes de trabajo en segundo plano...');
      },
      error: (error) => {
        console.error('Error al solicitar reporte:', error);
        this._toastService.showToast('error', 'Error al solicitar el reporte');
        this.loading = false;
      }
    });
  }

  // Getters para mostrar la selección actual
  get selectedProjectsNames(): string {
    const selectedIds = this.reportForm.value.projectIds || [];
    if (selectedIds.length === 0) return 'Todos los proyectos';
    
    const selectedProjects = this.projectsDropdown.filter(p => selectedIds.includes(p.value));
    if (selectedProjects.length <= 2) {
      return selectedProjects.map(p => p.label).join(', ');
    } else {
      return `${selectedProjects.length} proyectos seleccionados`;
    }
  }
}


